import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { toast } from "sonner";

import {

  BellRinging,

  CalendarBlank,

  CalendarX,

  ChatsCircle,

  CheckCircle,

  CreditCard,

  DownloadSimple,

  DotsThreeCircle,

  LockKey,

  House,

  ImagesSquare,

  Megaphone,

  Paperclip,

  SignOut,

  UserCircle,

  Wallet,

  XCircle,

} from "@phosphor-icons/react";

import { useNavigate } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";

import { ParentSchedule } from "@/pages/Timetable";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import api, { API, formatErr, fmtDate, inr, openFile, todayLocal } from "@/lib/api";

import ChangePassword from "@/components/ChangePassword";

import ReportCard from "@/components/ReportCard";

import Student360View from "@/components/Student360View";

import ParentAttendanceCalendar from "@/components/attendance/ParentAttendanceCalendar";

import ParentMessages from "@/components/messaging/ParentMessages";

import { ParentMoments } from "@/components/MediaMoments";

import NotificationBell from "@/components/NotificationBell";

import LanguageSelector from "@/components/LanguageSelector";



const catColor = {

  holiday: "bg-status-blue/10 text-status-blue",

  event: "bg-brand-mint/15 text-teal-700",

  fee_reminder: "bg-status-amber/20 text-amber-700",

  birthday: "bg-brand-coral/10 text-brand-coralDark",

  general: "bg-slate-100 text-slate-600",

};



const HOMEWORK_LANGUAGE_LABELS = {
  en: "English",
  hi: "हिन्दी",
  kn: "ಕನ್ನಡ",
  mr: "मराठी",
  te: "తెలుగు",
};

export default function Parent() {

  const { user, logout } = useAuth();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const [feed, setFeed] = useState(null);

  const [tab, setTab] = useState("home");

  const [childIdx, setChildIdx] = useState(0);

  const [paying, setPaying] = useState(null);

  const [receiptBusy, setReceiptBusy] = useState(null);

  const [showChangePw, setShowChangePw] = useState(false);

  const [externalOpen, setExternalOpen] = useState(false);

  const [externalForm, setExternalForm] = useState({ invoice_id: "", amount: "", method: "upi", payment_date: todayLocal(), reference: "", note: "" });

  const [manualPreview, setManualPreview] = useState(null);

  const [paymentQrSrc, setPaymentQrSrc] = useState("");

  const [moreOpen, setMoreOpen] = useState(false);



  const downloadReceipt = async (paymentId) => {

    setReceiptBusy(paymentId);

    try {

      const res = await api.get(`/parent/fees/receipt/payment/${paymentId}`, { responseType: "blob" });

      const url = URL.createObjectURL(res.data);

      const a = document.createElement("a");

      a.href = url;

      a.download = `fee_receipt_${paymentId.slice(0, 8)}.pdf`;

      a.click();

      URL.revokeObjectURL(url);

    } catch (e) {

      toast.error(formatErr(e, t("parentFees.receiptUnavailable", { defaultValue: "Receipt not available yet" })));

    } finally {

      setReceiptBusy(null);

    }

  };

  const [leaves, setLeaves] = useState([]);

  const [leaveOpen, setLeaveOpen] = useState(false);

  const [leaveForm, setLeaveForm] = useState({ from_date: "", to_date: "", reason: "" });



  const load = () => {

    api.get("/parent/feed").then((r) => setFeed(r.data)).catch((e) => toast.error(formatErr(e)));

  };

  const loadLeaves = () => {

    api.get("/parent/leaves").then((r) => setLeaves(r.data)).catch(() => {});

  };



  const [reportFor, setReportFor] = useState(null);



  useEffect(() => {

    loadLeaves();

  }, []);



  useEffect(() => {

    if (tab === "home") load();

  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps



  useEffect(() => {

    const onVisible = () => {

      if (document.visibilityState === "visible") load();

    };

    document.addEventListener("visibilitychange", onVisible);

    return () => document.removeEventListener("visibilitychange", onVisible);

  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const path = feed?.payment_settings?.payment_qr_url;
    if (!path) {
      setPaymentQrSrc("");
      return undefined;
    }
    let url = "";
    let cancelled = false;
    api.get("/files/download", { params: { path }, responseType: "blob" })
      .then(({ data }) => {
        if (!cancelled) {
          url = URL.createObjectURL(data);
          setPaymentQrSrc(url);
        }
      })
      .catch(() => setPaymentQrSrc(""));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [feed?.payment_settings?.payment_qr_url]);



  const child = feed?.children?.[childIdx];

  const todayStr = useMemo(() => todayLocal(), []);

  const todayAtt = child?.attendance?.find((a) => a.date === todayStr);



  const submitLeave = async (e) => {

    e.preventDefault();

    try {

      await api.post("/parent/leaves", { ...leaveForm, student_id: child.id });

      toast.success(t("parentLeave.submittedSuccess", { defaultValue: "Leave request submitted to the school" }));

      setLeaveOpen(false);

      setLeaveForm({ from_date: "", to_date: "", reason: "" });

      loadLeaves();

    } catch (err) {

      toast.error(formatErr(err));

    }

  };



  const pay = async (invoiceId) => {

    setPaying(invoiceId);

    try {

      if (!feed.payment_settings?.online_payment_enabled) {
        throw new Error("Online gateway payment is disabled");
      }
      const invoice = child.invoices.find((row) => row.id === invoiceId);
      const amount = Math.max(0, Number(invoice?.amount || 0) - Number(invoice?.paid_amount || 0) - Number(invoice?.concession_amount || 0));
      const { data: preview } = await api.post("/parent/fees/payment-preview", { student_id: child.id, amount });
      const { data } = await api.post("/parent/fees/pay", {
        student_id: child.id,
        amount,
        allocations: preview.allocations.map((row) => ({ invoice_id: row.invoice_id, amount: row.amount })),
        origin_url: window.location.origin,
        provider: feed.payment_settings?.payment_provider || "stripe",
      });
      if (data.provider === "razorpay") {
        await new Promise((resolve, reject) => {
          if (window.Razorpay) return resolve();
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
        const checkout = new window.Razorpay({
          key: data.key_id,
          amount: data.amount_paise,
          currency: data.currency,
          order_id: data.order_id,
          name: feed.school_name || "School fee",
          handler: async (response) => {
            try {
              await api.post("/parent/fees/razorpay/verify", response);
              toast.success("Payment received");
              load();
            } catch (error) {
              toast.error(formatErr(error, "Payment verification failed"));
            } finally {
              setPaying(null);
            }
          },
          modal: { ondismiss: () => setPaying(null) },
        });
        checkout.open();
      } else {
        window.location.href = data.checkout_url;
      }

    } catch (e) {

      toast.error(formatErr(e));

      setPaying(null);

    }

  };



  const reportExternalPayment = async (e) => {

    e.preventDefault();

    try {

      const amount = Number(externalForm.amount);
      const { data: preview } = await api.post("/parent/fees/payment-preview", { student_id: child.id, amount });
      await api.post("/parent/fees/manual-intent", {
        student_id: child.id,
        amount,
        allocations: preview.allocations.map((row) => ({ invoice_id: row.invoice_id, amount: row.amount })),
        method: externalForm.method,
        payment_date: externalForm.payment_date,
        reference: externalForm.reference.trim(),
        note: externalForm.note || null,
      });

      toast.success("Payment details submitted to school for confirmation");

      setExternalOpen(false);

      setExternalForm({ invoice_id: "", amount: "", method: "upi", payment_date: todayLocal(), reference: "", note: "" });

      setManualPreview(null);

      load();

    } catch (e) { toast.error(formatErr(e)); }

  };



  const confirmSchoolPayment = async (paymentId) => {

    try {

      await api.post(`/parent/fees/payments/${paymentId}/confirm`);

      toast.success("Payment confirmed");

      load();

    } catch (e) { toast.error(formatErr(e)); }

  };



  const flagPaymentMismatch = async (paymentId) => {

    try {

      await api.post(`/parent/fees/payments/${paymentId}/mismatch`);

      toast.success("Payment marked for school review");

      load();

    } catch (e) { toast.error(formatErr(e)); }

  };



  if (!feed) {

    return <div className="min-h-screen grid place-items-center bg-[#F4F5F7]"><div className="w-10 h-10 rounded-full border-4 border-brand-coral border-t-transparent animate-spin" /></div>;

  }



  const primaryTabs = [
    {
      id: "home",
      label: t("parentNavigation.home", { defaultValue: "Home" }),
      icon: House,
    },
    {
      id: "profile",
      label: t("student360.myChild", { defaultValue: "My Child" }),
      icon: UserCircle,
    },
    {
      id: "fees",
      label: t("parentNavigation.fees", { defaultValue: "Fees" }),
      icon: Wallet,
    },
    {
      id: "messages",
      label: t("parentNavigation.messages", { defaultValue: "Messages" }),
      icon: ChatsCircle,
    },
  ];

  const moreTabs = [
    {
      id: "notices",
      label: t("parentNavigation.notices", { defaultValue: "Notices" }),
      icon: Megaphone,
    },
    {
      id: "moments",
      label: t("parentNavigation.moments", { defaultValue: "Moments" }),
      icon: ImagesSquare,
    },
    {
      id: "schedule",
      label: t("parentNavigation.schedule", { defaultValue: "Schedule" }),
      icon: CalendarBlank,
    },
  ];

  const isMoreActive =
    moreOpen || moreTabs.some((item) => item.id === tab);



  return (

    <div className="min-h-screen bg-[#E8EAF0] md:py-8" data-testid="parent-feed">

      <div className="max-w-md mx-auto bg-[#FDFDFD] min-h-screen md:min-h-[85vh] md:rounded-[2rem] md:shadow-[0_20px_60px_rgb(0,0,0,0.12)] md:border md:border-slate-200 flex flex-col relative overflow-hidden">

        {/* Header */}

        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/85 border-b border-white/40 px-5 py-4">

          <div className="flex items-center justify-between">

            <div>

              <p className="text-xs text-slate-500">{feed.school_name}</p>

              <h1 className="font-heading text-lg font-extrabold" data-testid="parent-greeting">{t("parentHome.greeting", { defaultValue: "Hi, {{name}}", name: user?.name?.split(" ")[0] })}</h1>

            </div>

            <div className="flex items-center gap-1">

              <LanguageSelector  />

              <NotificationBell onNavigate={setTab} />

              <button

                data-testid="parent-change-password-btn"

                onClick={() => setShowChangePw(true)}

                className="p-2 text-slate-400 hover:text-brand-coral transition-colors duration-200"

                title={t("account.changePassword", { defaultValue: "Change Password" })}

              >

                <LockKey size={20} />

              </button>

              <button

                data-testid="parent-logout-btn"

                onClick={() => { logout(); navigate("/login"); }}

                className="p-2 text-slate-400 hover:text-brand-coral transition-colors duration-200"

              >

                <SignOut size={20} />

              </button>

            </div>

            <ChangePassword open={showChangePw} onClose={() => setShowChangePw(false)} />

          </div>

          {feed.children.length > 1 && (

            <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-none">

              {feed.children.map((c, i) => (

                <button

                  key={c.id}

                  data-testid={`child-chip-${c.id}`}

                  onClick={() => setChildIdx(i)}

                  className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors duration-200 ${

                    i === childIdx ? "bg-brand-coral text-white" : "bg-slate-100 text-slate-600"

                  }`}

                >

                  {c.name.split(" ")[0]}

                </button>

              ))}

            </div>

          )}

        </header>



        {/* Content */}

        <main className="flex-1 px-5 py-5 pb-24 overflow-y-auto">

          {tab === "home" && child && (

            <div className="space-y-5">

              {/* Today attendance */}

              <div

                className={`rounded-2xl p-5 border ${

                  !todayAtt

                    ? "bg-slate-50 border-slate-200"

                    : todayAtt.status === "present"

                    ? "bg-status-green/10 border-status-green/30"

                    : "bg-status-red/10 border-status-red/30"

                }`}

                data-testid="today-attendance-card"

              >

                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t("parentHome.todayClass", { defaultValue: "Today · {{className}}", className: child.class_name })}</p>

                <div className="flex items-center gap-3 mt-2">

                  {!todayAtt ? (

                    <p className="font-heading font-bold text-slate-500">{t("parentHome.attendanceNotMarked", { defaultValue: "Attendance not marked yet" })}</p>

                  ) : todayAtt.status === "present" ? (

                    <>

                      <CheckCircle size={28} weight="duotone" className="text-status-green" />

                      <p className="font-heading text-lg font-extrabold text-status-green">{t("parentHome.isPresent", { defaultValue: "{{name}} is present", name: child.name.split(" ")[0] })}</p>

                    </>

                  ) : (

                    <>

                      <XCircle size={28} weight="duotone" className="text-status-red" />

                      <p className="font-heading text-lg font-extrabold text-status-red">{t("parentHome.isAbsent", { defaultValue: "{{name}} is absent", name: child.name.split(" ")[0] })}</p>

                    </>

                  )}

                </div>

              </div>



              {/* Attendance calendar */}

              <ParentAttendanceCalendar child={child} />



              {/* Alerts */}

              {feed.notifications.length > 0 && (

                <section>

                  <h2 className="font-heading font-bold text-sm text-slate-400 uppercase tracking-wider mb-2.5">{t("parentHome.alerts", { defaultValue: "Alerts" })}</h2>

                  <div className="space-y-2">

                    {feed.notifications.slice(0, 5).map((n) => (

                      <div key={n.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex gap-3" data-testid={`alert-${n.id}`}>

                        <BellRinging size={20} weight="duotone" className={n.kind === "attendance" ? "text-status-red shrink-0" : "text-status-blue shrink-0"} />

                        <div>

                          <p className="text-sm font-semibold">{n.title}</p>

                          <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>

                        </div>

                      </div>

                    ))}

                  </div>

                </section>

              )}



              {/* Homework feed */}

              <section>

                <h2 className="font-heading font-bold text-sm text-slate-400 uppercase tracking-wider mb-2.5">{t("parentHome.homeworkActivities", { defaultValue: "Homework & activities" })}</h2>

                <div className="space-y-2.5">

                  {child.homework.map((h) => (

                    <div key={h.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`parent-homework-${h.id}`}>

                      <div className="flex items-center gap-2">

                        <span className="text-[10px] font-extrabold uppercase tracking-wide bg-brand-coral/10 text-brand-coralDark px-2 py-0.5 rounded-full">{h.kind}</span>

                        <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {HOMEWORK_LANGUAGE_LABELS[h.content_language || "en"] || "English"}
                        </span>

                        <span className="text-[10px] font-semibold text-slate-400">{h.subject} · {fmtDate(h.created_at)}</span>

                      </div>

                      <p className="font-heading font-bold mt-2 text-sm">{h.title}</p>

                      <p
                        lang={h.content_language || "en"}
                        className="text-sm text-slate-600 mt-1 whitespace-pre-wrap"
                      >
                        {h.content}
                      </p>

                    </div>

                  ))}

                  {child.homework.length === 0 && <p className="text-sm text-slate-400">{t("parentHome.noHomework", { defaultValue: "No homework posted yet." })}</p>}

                </div>

              </section>



              {/* Leave application */}

              <section>

                <div className="flex items-center justify-between mb-2.5">

                  <h2 className="font-heading font-bold text-sm text-slate-400 uppercase tracking-wider">{t("parentLeave.heading", { defaultValue: "Leave" })}</h2>

                  <button

                    data-testid="apply-leave-btn"

                    onClick={() => {

                      if (!leaveOpen) setLeaveForm((f) => ({ ...f, from_date: f.from_date || todayStr, to_date: f.to_date || todayStr }));

                      setLeaveOpen(!leaveOpen);

                    }}

                    className="text-xs font-bold text-brand-coralDark bg-brand-coral/10 px-3 py-1.5 rounded-full flex items-center gap-1 transition-colors duration-200 hover:bg-brand-coral/20"

                  >

                    <CalendarX size={13} /> {leaveOpen ? t("common.cancel", { defaultValue: "Cancel" }) : t("parentLeave.applyForLeave", { defaultValue: "Apply for leave" })}

                  </button>

                </div>

                {leaveOpen && (

                  <form onSubmit={submitLeave} className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3 mb-3" data-testid="parent-leave-form">

                    <div className="grid grid-cols-2 gap-3">

                      <Input type="date" data-testid="leave-from" required value={leaveForm.from_date} onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })} />

                      <Input type="date" data-testid="leave-to" required value={leaveForm.to_date} onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })} />

                    </div>

                    <Input data-testid="leave-reason" required placeholder={t("parentLeave.reasonPlaceholder", { defaultValue: "Reason (e.g. fever, family trip)" })} value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} />

                    <Button type="submit" data-testid="leave-submit-btn" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white text-sm font-bold transition-transform duration-300 active:scale-95">

                      {t("parentLeave.submitRequest", { defaultValue: "Submit leave request" })}

                    </Button>

                  </form>

                )}

                {leaves.filter((l) => l.student_id === child.id).length > 0 && (

                  <div className="space-y-2">

                    {leaves.filter((l) => l.student_id === child.id).slice(0, 3).map((l) => (

                      <div key={l.id} className="bg-white border border-slate-200 rounded-2xl p-3.5 flex items-center justify-between" data-testid={`my-leave-${l.id}`}>

                        <div>

                          <p className="text-sm font-semibold">{fmtDate(l.from_date)} → {fmtDate(l.to_date)}</p>

                          <p className="text-xs text-slate-500">{l.reason}</p>

                        </div>

                        <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${l.status === "approved" ? "bg-status-green/10 text-status-green" : l.status === "rejected" ? "bg-status-red/10 text-status-red" : "bg-status-amber/20 text-amber-700"}`}>

                          {t(`parentLeave.status.${l.status}`, { defaultValue: l.status })}

                        </span>

                      </div>

                    ))}

                  </div>

                )}

              </section>



              {/* Exam results */}

              {(child.exams?.length > 0 || child.published_terms?.length > 0) && (

                <section>

                  <div className="flex items-center justify-between mb-2.5">

                    <h2 className="font-heading font-bold text-sm text-slate-400 uppercase tracking-wider">{t("parentExams.heading", { defaultValue: "Exam results" })}</h2>

                    {child.published_terms?.length > 0 && (

                      <button

                        data-testid="parent-report-card-btn"

                        onClick={() => setReportFor(child.id)}

                        className="text-xs font-bold text-brand-coralDark bg-brand-coral/10 rounded-full px-3 py-1.5 hover:bg-brand-coral/20 transition-colors duration-200"

                      >

                        {t("parentExams.viewReportCard", { defaultValue: "View report card" })}

                      </button>

                    )}

                  </div>

                  {child.exams?.length > 0 && (

                  <div className="space-y-2">

                    {child.exams.map((e) => (

                      <div key={e.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between" data-testid={`parent-exam-${e.id}`}>

                        <div>

                          <p className="font-heading font-bold text-sm">{e.name}</p>

                          <p className="text-xs text-slate-500">{e.subject} · {fmtDate(e.exam_date)}</p>

                        </div>

                        {e.marks != null ? (

                          <span className="font-heading font-extrabold text-brand-coralDark">{e.marks}/{e.max_marks}</span>

                        ) : (

                          <span className="text-xs text-slate-400">{t("parentExams.awaitingMarks", { defaultValue: "Awaiting marks" })}</span>

                        )}

                      </div>

                    ))}

                  </div>

                  )}

                </section>

              )}

            </div>

          )}



          {tab === "notices" && (

            <div className="space-y-2.5" data-testid="parent-notices">

              {feed.broadcasts.map((b) => (

                <div key={b.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`notice-${b.id}`}>

                  <span className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${catColor[b.category] || catColor.general}`}>

                    {t(`parentNotices.categories.${b.category}`, { defaultValue: b.category.replace("_", " ") })}

                  </span>

                  <p className="font-heading font-bold mt-2 text-sm">{b.title}</p>

                  <p className="text-sm text-slate-600 mt-1">{b.message}</p>

                  {b.attachments?.length > 0 && (

                    <div className="flex flex-wrap gap-1.5 mt-2.5">

                      {b.attachments.map((a) => (

                        <a

                          key={a.path}

                          href="#"

                          onClick={(e) => { e.preventDefault(); openFile(a.path); }}

                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-status-blue bg-status-blue/10 rounded-full px-2.5 py-1"

                          data-testid={`notice-attachment-${a.name}`}

                        >

                          <Paperclip size={11} /> {a.name}

                        </a>

                      ))}

                    </div>

                  )}

                  <p className="text-[10px] text-slate-400 mt-2">{fmtDate(b.created_at)}</p>

                </div>

              ))}

              {feed.broadcasts.length === 0 && <p className="text-sm text-slate-400">{t("parentNotices.noAnnouncements", { defaultValue: "No announcements yet." })}</p>}

            </div>

          )}



          {tab === "messages" && <ParentMessages />}



          {tab === "profile" && child && <Student360View studentId={child.id} variant="parent" />}



          {tab === "moments" && child && <ParentMoments child={child} />}



          {tab === "schedule" && child && <ParentSchedule child={child} />}



          {tab === "fees" && child && (

            <div className="space-y-3" data-testid="parent-fees">

              <p className="text-sm text-slate-500">{t("parentFees.ledgerFor", { defaultValue: "Fee ledger for" })} <span className="font-bold text-slate-700">{child.name}</span></p>

              {feed.payment_settings?.manual_payment_enabled && <div className="bg-white border border-slate-200 rounded-2xl p-4">

                <div className="flex items-center justify-between gap-3">

                  <div><p className="font-heading font-bold text-sm">Pay by school QR / bank transfer</p><p className="text-xs text-slate-500 mt-1">Pay to the school, then submit the UTR once. Fees update only after school verification.</p></div>

                  <Button variant="outline" onClick={() => setExternalOpen(!externalOpen)} className="rounded-full shrink-0">{externalOpen ? "Close" : "Scan QR & report payment"}</Button>

                </div>

                {externalOpen && <form onSubmit={reportExternalPayment} className="grid sm:grid-cols-2 gap-3 mt-4">

                  <div className="sm:col-span-2 grid sm:grid-cols-[160px_1fr] gap-4 bg-slate-50 rounded-xl p-3">
                    {paymentQrSrc ? <img src={paymentQrSrc} alt="School payment QR" className="w-40 h-40 object-contain bg-white rounded-lg border" /> : <div className="w-40 h-24 grid place-items-center bg-white rounded-lg border text-xs text-slate-400">QR not uploaded</div>}
                    <div className="text-xs space-y-1"><p className="font-bold text-sm">{feed.payment_settings?.payment_account_holder || feed.school_name}</p>{feed.payment_settings?.payment_upi_id && <p>UPI: <b>{feed.payment_settings.payment_upi_id}</b></p>}{feed.payment_settings?.payment_bank_name && <p>Bank: {feed.payment_settings.payment_bank_name}</p>}{feed.payment_settings?.payment_account_number && <p>Account: {feed.payment_settings.payment_account_number}</p>}{feed.payment_settings?.payment_ifsc && <p>IFSC: {feed.payment_settings.payment_ifsc}</p>}{feed.payment_settings?.payment_instructions && <p className="text-slate-500 pt-1">{feed.payment_settings.payment_instructions}</p>}</div>
                  </div>

                  <div><label className="text-xs font-semibold text-slate-600">Amount (₹)</label><Input className="mt-1" type="number" min="1" required value={externalForm.amount} onChange={(e) => { setExternalForm({ ...externalForm, amount: e.target.value }); setManualPreview(null); }} onBlur={async () => { const amount = Number(externalForm.amount); if (amount > 0) { try { const { data } = await api.post("/parent/fees/payment-preview", { student_id: child.id, amount }); setManualPreview(data); } catch (error) { toast.error(formatErr(error)); } } }} /></div>

                  <div><label className="text-xs font-semibold text-slate-600">Mode</label><select className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" value={externalForm.method} onChange={(e) => setExternalForm({ ...externalForm, method: e.target.value })}><option value="upi">UPI / Static QR</option><option value="bank_transfer">Bank Transfer</option><option value="net_banking">Net Banking</option></select></div>

                  <div><label className="text-xs font-semibold text-slate-600">Payment date</label><Input className="mt-1" type="date" required value={externalForm.payment_date} onChange={(e) => setExternalForm({ ...externalForm, payment_date: e.target.value })} /></div>

                  <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-600">UTR / bank reference</label><Input className="mt-1" required value={externalForm.reference} onChange={(e) => setExternalForm({ ...externalForm, reference: e.target.value })} /></div>

                  {manualPreview && <div className="sm:col-span-2 rounded-xl border p-3"><p className="text-xs font-bold mb-2">This payment will be allocated as:</p>{manualPreview.allocations.map((row) => <div key={row.invoice_id} className="flex justify-between text-xs py-1"><span>{row.fee_category_name}</span><b>{inr(row.amount)}</b></div>)}<div className="flex justify-between text-sm border-t mt-2 pt-2"><b>Total</b><b>{inr(manualPreview.amount)}</b></div></div>}

                  <div className="sm:col-span-2"><Button type="submit" className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white">Submit to school for confirmation</Button></div>

                </form>}

              </div>}

              {(feed.pending_fee_payments || []).filter((p) => p.student_id === child.id).length > 0 && <div className="space-y-2">

                <p className="font-heading font-bold text-sm">Payments awaiting confirmation</p>

                {(feed.pending_fee_payments || []).filter((p) => p.student_id === child.id).map((p) => <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-4">

                  <div className="flex items-start justify-between gap-3"><div><p className="font-bold">{inr(p.amount)} · {(p.method || '').replaceAll('_',' ').toUpperCase()}</p><p className="text-xs text-slate-500 mt-1">{fmtDate(p.payment_date)} {p.reference ? `· ${p.reference}` : ''}</p><p className="text-xs mt-2">{p.reconciliation_status === 'pending_parent_confirmation' ? 'School has recorded this payment. Please confirm the details.' : p.reconciliation_status === 'pending_school_confirmation' ? 'Submitted by you. Waiting for school confirmation.' : 'Details do not match. School review required.'}</p></div>{p.reconciliation_status === 'pending_parent_confirmation' && <div className="flex gap-2"><Button size="sm" onClick={() => confirmSchoolPayment(p.id)} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white">Confirm</Button><Button size="sm" variant="outline" onClick={() => flagPaymentMismatch(p.id)} className="rounded-full">Mismatch</Button></div>}</div>

                </div>)}

              </div>}

              {(child.standalone_payments || []).map((payment) => (

                <div key={payment.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`standalone-payment-${payment.id}`}>

                  <div className="flex items-start justify-between gap-3">

                    <div>

                      <div className="flex items-center gap-2">

                        <p className="font-heading text-xl font-extrabold">{inr(payment.amount)}</p>

                        <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-status-green/10 text-status-green">Paid</span>

                      </div>

                      <p className="text-xs text-slate-500 mt-1">

                        {(payment.method || "payment").replaceAll("_", " ").toUpperCase()} · {fmtDate(payment.payment_date)}

                      </p>

                      {payment.receipt_no && <p className="text-[11px] text-slate-500 mt-2">Receipt {payment.receipt_no}</p>}

                      {payment.transaction_id && <p className="text-[11px] text-slate-400">Transaction {payment.transaction_id}</p>}

                      {payment.reference && <p className="text-[11px] text-slate-400">Reference {payment.reference}</p>}

                    </div>

                    <Button

                      variant="outline"

                      onClick={() => downloadReceipt(payment.id)}

                      disabled={receiptBusy === payment.id}

                      className="rounded-full border-slate-300 text-xs font-bold px-4 shrink-0"

                    >

                      <DownloadSimple size={14} className="mr-1.5" />

                      {receiptBusy === payment.id

                        ? t("parentFees.preparing", { defaultValue: "Preparing…" })

                        : t("parentFees.receipt", { defaultValue: "Receipt" })}

                    </Button>

                  </div>

                </div>

              ))}

              {child.invoices.filter((inv) => !["cancelled", "canceled", "void"].includes(String(inv.status || "").toLowerCase())).map((inv) => {

                const remaining = inv.amount - (inv.paid_amount || 0) - (inv.concession_amount || 0);

                return (

                  <div key={inv.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`fee-invoice-${inv.id}`}>

                    <div className="flex items-center justify-between">

                      <p className="font-heading font-bold text-sm">{inv.title}</p>

                      <span

                        className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${

                          inv.status === "paid" ? "bg-status-green/10 text-status-green" : inv.status === "partial" ? "bg-status-amber/20 text-amber-700" : "bg-status-red/10 text-status-red"

                        }`}

                      >

                        {t(`parentFees.status.${inv.status}`, { defaultValue: inv.status })}

                      </span>

                    </div>

                    <div className="flex items-end justify-between mt-3">

                      <div>

                        <p className="font-heading text-xl font-extrabold">{inr(inv.amount)}</p>

                        <p className="text-[11px] text-slate-400">{t("parentFees.due", { defaultValue: "due" })} {fmtDate(inv.due_date)}{inv.status === "partial" ? ` · ${inr(remaining)} ${t("parentFees.left", { defaultValue: "left" })}` : ""}</p>

                      </div>

                      <div className="flex items-center gap-2">

                        {(inv.receipts || []).map((receipt, receiptIndex) => (

                          <Button

                            key={receipt.id}

                            variant="outline"

                            data-testid={`fee-receipt-btn-${receipt.id}`}

                            onClick={() => downloadReceipt(receipt.id)}

                            disabled={receiptBusy === receipt.id}

                            title={receipt.receipt_no || "Receipt"}

                            className="rounded-full border-slate-300 text-xs font-bold px-4 transition-transform duration-300 active:scale-95"

                          >

                            <DownloadSimple size={14} className="mr-1.5" />

                            {receiptBusy === receipt.id ? t("parentFees.preparing", { defaultValue: "Preparing…" }) : `${t("parentFees.receipt", { defaultValue: "Receipt" })}${(inv.receipts || []).length > 1 ? ` ${receiptIndex + 1}` : ""}`}

                          </Button>

                        ))}

                        {inv.status !== "paid" && feed.payment_settings?.online_payment_enabled && (

                        <Button

                          data-testid={`fee-pay-btn-${inv.id}`}

                          onClick={() => pay(inv.id)}

                          disabled={paying === inv.id}

                          className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white text-xs font-bold px-5 transition-transform duration-300 active:scale-95"

                        >

                          <CreditCard size={14} className="mr-1.5" />

                          {paying === inv.id ? t("parentFees.opening", { defaultValue: "Opening…" }) : t("parentFees.payAmount", { defaultValue: "Pay {{amount}}", amount: inr(remaining) })}

                        </Button>

                        )}

                      </div>

                    </div>

                  </div>

                );

              })}

              {child.invoices.length === 0 && (child.standalone_payments || []).length === 0 && <p className="text-sm text-slate-400">{t("parentFees.noRecords", { defaultValue: "No fee records yet." })}</p>}

            </div>

          )}

          <ReportCard studentId={reportFor} onClose={() => setReportFor(null)} />

        </main>



        {/* Mobile More menu */}
        {moreOpen && (
          <>
            <button
              type="button"
              aria-label="Close more menu"
              className="absolute inset-0 z-30 bg-slate-900/20"
              onClick={() => setMoreOpen(false)}
            />

            <div
              className="absolute bottom-[76px] inset-x-3 z-40 rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl"
              data-testid="parent-more-menu"
            >
              <div className="grid grid-cols-3 gap-2">
                {moreTabs.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    data-testid={`parent-more-${item.id}`}
                    onClick={() => {
                      setTab(item.id);
                      setMoreOpen(false);
                    }}
                    className={`flex min-h-[74px] flex-col items-center justify-center gap-1 rounded-xl px-2 py-3 transition-colors ${
                      tab === item.id
                        ? "bg-brand-coral/10 text-brand-coral"
                        : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    <item.icon
                      size={24}
                      weight={tab === item.id ? "duotone" : "regular"}
                    />
                    <span className="text-[11px] font-bold">
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Five-item mobile bottom navigation */}
        <nav className="absolute bottom-0 inset-x-0 z-50 grid grid-cols-5 border-t border-slate-100 bg-white/95 px-1 pb-[max(0.625rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur-xl">
          {primaryTabs.map((item) => (
            <button
              type="button"
              key={item.id}
              data-testid={`parent-tab-${item.id}`}
              onClick={() => {
                setTab(item.id);
                setMoreOpen(false);
              }}
              className={`flex min-w-0 flex-col items-center gap-0.5 rounded-xl px-1 py-1 transition-colors duration-200 ${
                tab === item.id
                  ? "text-brand-coral"
                  : "text-slate-400"
              }`}
            >
              <item.icon
                size={22}
                weight={tab === item.id ? "duotone" : "regular"}
              />
              <span className="max-w-full truncate text-[10px] font-bold">
                {item.label}
              </span>
            </button>
          ))}

          <button
            type="button"
            data-testid="parent-tab-more"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((open) => !open)}
            className={`flex min-w-0 flex-col items-center gap-0.5 rounded-xl px-1 py-1 transition-colors duration-200 ${
              isMoreActive
                ? "text-brand-coral"
                : "text-slate-400"
            }`}
          >
            <DotsThreeCircle
              size={22}
              weight={isMoreActive ? "duotone" : "regular"}
            />
            <span className="text-[10px] font-bold">
              {t("parentNavigation.more", { defaultValue: "More" })}
            </span>
          </button>
        </nav>

      </div>

    </div>

  );

}
